// ─────────────────────────────────────────────────────────────
// CampaignWizard.jsx  —  src/components/CampaignWizard.jsx
// A 3-step modal wizard for creating/editing campaigns.
// Import and use in Email.js:
//   import CampaignWizard from '../components/CampaignWizard';
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from 'react';
import client from '../api/client';
import { C, Btn } from '../utils/styles';

const PROVIDERS_META = [
  { id:'custom_smtp', label:'Custom SMTP',         icon:'ti-settings-cog',  color:'#374151' },
  { id:'gmail',       label:'Gmail / Workspace',   icon:'ti-brand-google',  color:'#EA4335' },
  { id:'outlook',     label:'Outlook / Microsoft', icon:'ti-brand-windows', color:'#0078D4' },
  { id:'mailgun',     label:'Mailgun',              icon:'ti-mail-forward',  color:'#F06B66' },
  { id:'sendgrid',    label:'SendGrid',             icon:'ti-send',          color:'#1A82E2' },
];
const STAGES  = ['New','Contacted','Qualified','Proposal','Won','Lost'];
const SOURCES = ['Google Ads','Facebook Ads','Referral','Organic','Cold Outreach','WhatsApp','Other'];
const MERGE_FIELDS = ['{{first_name}}','{{last_name}}','{{name}}','{{email}}','{{company}}','{{phone}}','{{source}}','{{signature}}','{{unsubscribe_url}}'];
const TIMEZONES = ['Asia/Kolkata','Asia/Dubai','Asia/Singapore','Asia/Tokyo','Asia/Shanghai','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','Europe/London','Europe/Paris','Europe/Berlin','Europe/Moscow','Australia/Sydney','Pacific/Auckland','UTC'];

// ── Inline Rich Editor ─────────────────────────────────────────
const RichEditor = ({ value, onChange, minHeight=240 }) => {
  const editorRef = useRef(null);
  const [fmt, setFmt] = useState({});
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value||''))
      editorRef.current.innerHTML = value||'';
  }, []); // eslint-disable-line
  const exec = (cmd, val=null) => { editorRef.current?.focus(); document.execCommand(cmd,false,val); sync(); };
  const sync = useCallback(() => { if (editorRef.current) onChange(editorRef.current.innerHTML); },[onChange]);
  const chk  = () => setFmt({ bold:document.queryCommandState('bold'), italic:document.queryCommandState('italic'), underline:document.queryCommandState('underline') });
  const bs   = (a) => ({ padding:'5px 8px',borderRadius:6,border:'none',cursor:'pointer',fontSize:13,background:a?C.primary:'#f3f4f6',color:a?'#fff':C.dark });
  const insertUnsub = () => { editorRef.current?.focus(); document.execCommand('insertHTML',false,'<a href="{{unsubscribe_url}}" style="color:#9ca3af;font-size:12px;">Unsubscribe</a>'); sync(); };
  const insertLink  = () => { const u=window.prompt('URL:','https://'); if(u) exec('createLink',u); };
  const div = <div style={{width:1,height:20,background:'#e5e7eb',margin:'0 3px'}}/>;
  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
      <div style={{display:'flex',flexWrap:'wrap',gap:2,padding:'5px 8px',background:'#f9fafb',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
        <select onChange={e=>exec('formatBlock',e.target.value)} defaultValue="" style={{padding:'3px 6px',borderRadius:5,border:`1px solid ${C.border}`,fontSize:11,outline:'none',background:'#fff'}}>
          <option value="" disabled>Style</option>
          <option value="p">Paragraph</option>
          <option value="h1">H1</option>
          <option value="h2">H2</option>
          <option value="h3">H3</option>
        </select>
        {div}
        <button style={bs(fmt.bold)}      onClick={()=>exec('bold')}><i className="ti ti-bold"/></button>
        <button style={bs(fmt.italic)}    onClick={()=>exec('italic')}><i className="ti ti-italic"/></button>
        <button style={bs(fmt.underline)} onClick={()=>exec('underline')}><i className="ti ti-underline"/></button>
        {div}
        <button style={bs(false)} onClick={()=>exec('insertUnorderedList')}><i className="ti ti-list"/></button>
        <button style={bs(false)} onClick={()=>exec('insertOrderedList')}><i className="ti ti-list-numbers"/></button>
        {div}
        <button style={bs(false)} onClick={insertLink} title="Link"><i className="ti ti-link"/></button>
        <button style={bs(false)} onClick={insertUnsub} title="Unsubscribe link"><i className="ti ti-mail-off"/></button>
        {div}
        <input type="color" onChange={e=>exec('foreColor',e.target.value)} style={{width:24,height:24,borderRadius:4,border:`1px solid ${C.border}`,cursor:'pointer',padding:1}}/>
        {div}
        <button style={bs(false)} onClick={()=>exec('removeFormat')}><i className="ti ti-clear-formatting"/></button>
        <button style={{...bs(false),marginLeft:'auto',fontSize:11,padding:'3px 8px'}}
          onClick={()=>{const h=window.prompt('Paste HTML:','');if(h&&editorRef.current){editorRef.current.innerHTML=h;sync();}}}>
          ‹/›
        </button>
      </div>
      <div ref={editorRef} id="wiz-rich-editor" contentEditable suppressContentEditableWarning
        onInput={sync} onKeyUp={chk} onMouseUp={chk}
        style={{minHeight,padding:'14px 16px',outline:'none',fontSize:14,lineHeight:1.7,color:C.dark,overflowY:'auto',maxHeight:380}}/>
    </div>
  );
};

// ── Step indicator ─────────────────────────────────────────────
const Steps = ({ current, steps }) => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:0,padding:'0 24px 20px'}}>
    {steps.map((s,i)=>(
      <React.Fragment key={s.id}>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,minWidth:90}}>
          <div style={{width:32,height:32,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
            background: i<current?C.success : i===current?C.primary:'#e5e7eb',
            color: i<=current?'#fff':'#9ca3af',fontSize:13,fontWeight:700,transition:'all 0.2s'}}>
            {i<current ? <i className="ti ti-check" style={{fontSize:14}}/> : i+1}
          </div>
          <span style={{fontSize:11,fontWeight:i===current?600:400,color:i===current?C.primary:i<current?C.success:'#9ca3af',whiteSpace:'nowrap'}}>{s.label}</span>
        </div>
        {i<steps.length-1&&(
          <div style={{flex:1,height:2,background:i<current?C.success:'#e5e7eb',margin:'0 4px',marginBottom:16,transition:'all 0.2s'}}/>
        )}
      </React.Fragment>
    ))}
  </div>
);

// ── Main Wizard ────────────────────────────────────────────────
export default function CampaignWizard({
  editCampaign, providers, leads,
  onClose, onSent, onDraftSaved, notify,
}) {
  const STEPS = [
    { id:'compose',    label:'Compose'    },
    { id:'recipients', label:'Recipients' },
    { id:'schedule',   label:'Schedule'   },
  ];

  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [testAddr, setTestAddr] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [lastFocused, setLastFocused] = useState('body');

  // Compose state
  const [name,       setName]       = useState(editCampaign?.name || '');
  const [subject,    setSubject]    = useState(editCampaign?.subject || '');
  const [body,       setBody]       = useState(editCampaign?.body_html || '');
  const [providerId, setProviderId] = useState(providers.find(p=>p.is_default)?.id||'');
  const [attachments, setAttachments] = useState([]);

  // Recipients state
  const [recMode,    setRecMode]    = useState(editCampaign?.recipient_mode||'all');
  const [filterVal,  setFilterVal]  = useState(editCampaign?.filter_value||'');
  const [selLeads,   setSelLeads]   = useState(editCampaign?.selected_leads||[]);

  // Schedule state
  const [sendMode,       setSendMode]      = useState('now');
  const [scheduledDate,  setScheduledDate] = useState('');
  const [scheduledTime,  setScheduledTime] = useState('09:00');
  const [schedTz,        setSchedTz]       = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'Asia/Kolkata'; }
  });
  const [batchMode,      setBatchMode]     = useState('all');
  const [batchSize,      setBatchSize]     = useState(50);
  const [batchInterval,  setBatchInterval] = useState('hour');

  const recipientLeads = recMode==='all'?leads:recMode==='stage'?leads.filter(l=>l.stage===filterVal):recMode==='source'?leads.filter(l=>l.source===filterVal):recMode==='tag'?leads.filter(l=>(l.tags||[]).includes(filterVal)):leads;
  const recipientCount = recMode==='select'?selLeads.length:recipientLeads.filter(l=>!l.dnd).length;
  const dndCount       = leads.filter(l=>l.dnd).length;

  // Validate each step before allowing Next
  const canProceed = [
    subject.trim() && body.trim(),           // step 0: need subject + body
    recipientCount > 0,                       // step 1: need at least 1 recipient
    sendMode==='now' || (scheduledDate && scheduledTime), // step 2
  ];

  const insertMergeTag = (f) => {
    if (lastFocused==='subject') { setSubject(s=>s+f); return; }
    const ed = document.getElementById('wiz-rich-editor');
    if (!ed) { setBody(b=>b+f); return; }
    ed.focus();
    const sel = window.getSelection();
    if (sel&&sel.rangeCount>0) {
      const range=sel.getRangeAt(0); range.deleteContents();
      const node=document.createTextNode(f); range.insertNode(node);
      range.setStartAfter(node); range.collapse(true);
      sel.removeAllRanges(); sel.addRange(range);
      setBody(ed.innerHTML);
    } else { setBody(b=>b+f); }
  };

  const handleTestEmail = async () => {
    if (!testAddr.trim()) return notify('Enter an email address','error');
    if (!subject||!body) return notify('Add subject and body first','error');
    setTestSending(true);
    try {
      const { data } = await client.post('/api/email/send-test', { to:testAddr.trim(), subject, html:body, provider_id:providerId||undefined });
      notify(data.message||`Test sent to ${testAddr}`);
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setTestSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size>10*1024*1024) return notify('File must be under 10MB','error');
    setUploadingFile(true);
    try {
      const b64 = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(r.result.split(',')[1]); r.onerror=()=>rej(new Error('Read failed')); r.readAsDataURL(file); });
      const { data } = await client.post('/api/email/upload-attachment',{ filename:file.name, contentType:file.type, base64Data:b64 });
      setAttachments(a=>[...a,{ url:data.url, name:data.name }]);
      notify(`${file.name} attached!`);
    } catch (err) { notify(err.response?.data?.error||'Upload failed','error'); }
    finally { setUploadingFile(false); if(fileInputRef.current) fileInputRef.current.value=''; }
  };

  const buildPayload = (asDraft=false) => {
    const scheduledAt = sendMode==='scheduled'&&scheduledDate
      ? new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString() : null;
    const payload = {
      campaign_id:   editCampaign?.id||null,
      name:          name||subject,
      subject,
      html:          body,
      body_html:     body,
      provider_id:   providerId||undefined,
      attachments,
      scheduled_at:  scheduledAt,
      timezone:      schedTz,
      batch_size:    batchMode==='batch'?parseInt(batchSize)||50:0,
      batch_interval: batchInterval,
      recipient_mode: recMode,
      filter_value:   filterVal||null,
      selected_leads: selLeads.length?selLeads:null,
      status:         asDraft?'draft':undefined,
    };
    if (recMode==='select') payload.lead_ids = selLeads;
    else if (recMode!=='all') payload.filters = { [recMode]:filterVal };
    return payload;
  };

  const handleSend = async (asDraft=false) => {
    setSaving(true);
    try {
      if (asDraft) {
        const p = buildPayload(true);
        if (editCampaign) await client.patch(`/api/email/campaigns/${editCampaign.id}`, p);
        else await client.post('/api/email/campaigns', p);
        notify('Draft saved!'); onDraftSaved();
      } else {
        const { data } = await client.post('/api/email/campaign/send', buildPayload(false));
        notify(`✅ ${data.message}`); onSent();
      }
      onClose();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setSaving(false); }
  };

  const scheduledAt = sendMode==='scheduled'&&scheduledDate
    ? new Date(`${scheduledDate}T${scheduledTime}:00`) : null;

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflowY:'auto'}}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:760,maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 25px 80px rgba(0,0,0,0.3)',margin:'auto'}}>

        {/* ── Header ── */}
        <div style={{padding:'20px 24px 0',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
            <div>
              <div style={{fontWeight:700,fontSize:17,color:C.dark}}>{editCampaign?'Edit Campaign':'New Campaign'}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:2}}>
                {step===0&&'Compose your email'}
                {step===1&&'Choose who receives it'}
                {step===2&&'Schedule and send'}
              </div>
            </div>
            <button onClick={onClose} style={{background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',fontSize:18,color:C.muted}}><i className="ti ti-x"/></button>
          </div>
          <Steps current={step} steps={STEPS}/>
        </div>

        {/* ── Step content ── */}
        <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>

          {/* ── STEP 0: Compose ── */}
          {step===0&&(
            <div style={{display:'grid',gap:14}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Campaign Name <span style={{color:C.faint,fontWeight:400}}>(optional)</span></label>
                  <input style={inp} placeholder="e.g. July Follow-Up" value={name} onChange={e=>setName(e.target.value)}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Subject Line <span style={{color:C.danger}}>*</span></label>
                  <input style={inp} placeholder="Subject your contacts will see" value={subject} onChange={e=>setSubject(e.target.value)} onFocus={()=>setLastFocused('subject')}/>
                </div>
              </div>

              {providers.length>1&&(
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Send From</label>
                  <select style={inp} value={providerId} onChange={e=>setProviderId(e.target.value)}>
                    <option value="">Default ({providers.find(p=>p.is_default)?.from_email})</option>
                    {providers.map(p=><option key={p.id} value={p.id}>{p.from_email} ({PROVIDERS_META.find(x=>x.id===p.provider)?.label})</option>)}
                  </select>
                </div>
              )}

              {/* Merge tags */}
              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>
                  Merge Tags <span style={{color:C.faint,fontWeight:400}}>— inserting into <strong style={{color:C.primary}}>{lastFocused==='subject'?'Subject':'Body'}</strong></span>
                </label>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {MERGE_FIELDS.map(f=>(
                    <button key={f} onClick={()=>insertMergeTag(f)}
                      style={{padding:'3px 10px',borderRadius:20,border:`1px solid ${['{{signature}}','{{unsubscribe_url}}'].includes(f)?'#f59e0b':C.primary}`,background:['{{signature}}','{{unsubscribe_url}}'].includes(f)?'#fffbeb':'var(--color-primary-light)',color:['{{signature}}','{{unsubscribe_url}}'].includes(f)?'#92400e':C.primary,fontSize:11,cursor:'pointer',fontWeight:500}}>
                      {f}
                    </button>
                  ))}
                </div>
                <div style={{fontSize:11,color:C.faint,marginTop:5}}>
                  <code>{'{{signature}}'}</code> controls placement. <code>{'{{unsubscribe_url}}'}</code> use as a link href. If not placed, both auto-append at bottom.
                </div>
              </div>

              {/* Body */}
              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>Email Body <span style={{color:C.danger}}>*</span></label>
                <div onFocus={()=>setLastFocused('body')}>
                  <RichEditor value={body} onChange={setBody}/>
                </div>
              </div>

              {/* Attachments */}
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <label style={{fontSize:12,fontWeight:500,color:C.text}}>Attachments</label>
                  <div>
                    <input ref={fileInputRef} type="file" style={{display:'none'}} onChange={handleFileUpload}/>
                    <button onClick={()=>fileInputRef.current?.click()} disabled={uploadingFile}
                      style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 12px',fontSize:11,color:C.primary,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                      <i className={`ti ${uploadingFile?'ti-loader ti-spin':'ti-upload'}`} style={{fontSize:12}}/>{uploadingFile?'Uploading...':'Upload File'}
                    </button>
                  </div>
                </div>
                {!attachments.length?(
                  <div style={{fontSize:12,color:C.faint}}>No attachments. Max 10MB per file.</div>
                ):attachments.map((a,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#f9fafb',borderRadius:7,marginBottom:4,border:`1px solid ${C.border}`}}>
                    <i className="ti ti-paperclip" style={{color:C.muted,fontSize:14}}/><span style={{flex:1,fontSize:12}}>{a.name}</span>
                    <button onClick={()=>setAttachments(x=>x.filter((_,j)=>j!==i))} style={{padding:'1px 5px',borderRadius:4,border:'none',background:'#fef2f2',color:C.danger,cursor:'pointer'}}><i className="ti ti-x"/></button>
                  </div>
                ))}
              </div>

              {/* Test email */}
              <div style={{background:'#f9fafb',borderRadius:8,border:`1px solid ${C.border}`,padding:'12px 14px'}}>
                <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                  <i className="ti ti-mail-forward" style={{color:C.primary,fontSize:14}}/> Send Test Email
                </div>
                <div style={{display:'flex',gap:8}}>
                  <input type="email" placeholder="your@email.com" value={testAddr} onChange={e=>setTestAddr(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&handleTestEmail()}
                    style={{flex:1,...inp}}/>
                  <Btn size="sm" onClick={handleTestEmail} disabled={testSending||!testAddr.trim()||!subject||!body}>
                    <i className={`ti ${testSending?'ti-loader ti-spin':'ti-send'}`}/>{testSending?'Sending...':'Send Test'}
                  </Btn>
                </div>
                <div style={{fontSize:11,color:C.faint,marginTop:5}}>Merge tags replaced with sample data. Prefixed [TEST].</div>
              </div>
            </div>
          )}

          {/* ── STEP 1: Recipients ── */}
          {step===1&&(
            <div style={{display:'grid',gap:16}}>
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:10}}>
                <i className="ti ti-users" style={{color:C.success,fontSize:18}}/>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:'#166534'}}>{recipientCount} contact{recipientCount!==1?'s':''} will receive this email</div>
                  {dndCount>0&&<div style={{fontSize:12,color:'#16a34a'}}>{dndCount} DND contact{dndCount!==1?'s':''} automatically excluded</div>}
                </div>
              </div>

              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:10,display:'block'}}>Filter recipients by</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {[
                    { id:'all',    label:`All Contacts (${leads.filter(l=>!l.dnd).length})`, icon:'ti-users',      desc:'Send to everyone' },
                    { id:'stage',  label:'By Stage',                                          icon:'ti-filter',     desc:'Filter by pipeline stage' },
                    { id:'source', label:'By Source',                                         icon:'ti-antenna',    desc:'Filter by lead source' },
                    { id:'tag',    label:'By Tag',                                            icon:'ti-tag',        desc:'Filter by tag' },
                    { id:'select', label:'Select Manually',                                   icon:'ti-list-check', desc:'Hand-pick contacts' },
                  ].map(m=>(
                    <button key={m.id} onClick={()=>{ setRecMode(m.id); setFilterVal(''); setSelLeads([]); }}
                      style={{padding:'12px 14px',borderRadius:10,border:`1px solid ${recMode===m.id?C.primary:C.border}`,background:recMode===m.id?'var(--color-primary-light)':'#fff',cursor:'pointer',textAlign:'left',display:'flex',gap:10,alignItems:'flex-start'}}>
                      <i className={`ti ${m.icon}`} style={{fontSize:18,color:recMode===m.id?C.primary:C.muted,flexShrink:0,marginTop:1}}/>
                      <div>
                        <div style={{fontWeight:600,fontSize:13,color:recMode===m.id?C.primary:C.dark}}>{m.label}</div>
                        <div style={{fontSize:11,color:C.faint,marginTop:2}}>{m.desc}</div>
                      </div>
                      {recMode===m.id&&<i className="ti ti-circle-check" style={{color:C.primary,fontSize:16,marginLeft:'auto',flexShrink:0}}/>}
                    </button>
                  ))}
                </div>
              </div>

              {recMode==='stage'&&(
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Select Stage</label>
                  <select style={inp} value={filterVal} onChange={e=>setFilterVal(e.target.value)}>
                    <option value="">Choose a stage...</option>
                    {STAGES.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {recMode==='source'&&(
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Select Source</label>
                  <select style={inp} value={filterVal} onChange={e=>setFilterVal(e.target.value)}>
                    <option value="">Choose a source...</option>
                    {SOURCES.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {recMode==='tag'&&(
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Enter Tag</label>
                  <input style={inp} placeholder="Type tag exactly..." value={filterVal} onChange={e=>setFilterVal(e.target.value)}/>
                </div>
              )}
              {recMode==='select'&&(
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:8,display:'block'}}>{selLeads.length} contact{selLeads.length!==1?'s':''} selected</label>
                  <div style={{border:`1px solid ${C.border}`,borderRadius:8,maxHeight:280,overflowY:'auto'}}>
                    {leads.map(l=>(
                      <label key={l.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',cursor:l.dnd?'not-allowed':'pointer',borderBottom:'1px solid #f3f4f6',opacity:l.dnd?0.45:1,background:selLeads.includes(l.id)?'#eff6ff':'#fff'}}>
                        <input type="checkbox" checked={selLeads.includes(l.id)} disabled={l.dnd} style={{accentColor:C.primary}}
                          onChange={e=>setSelLeads(s=>e.target.checked?[...s,l.id]:s.filter(x=>x!==l.id))}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:500}}>{[l.first_name,l.last_name].filter(Boolean).join(' ')||l.name}</div>
                          <div style={{fontSize:11,color:C.muted}}>{l.email}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          {l.dnd&&<span style={{background:'#fef2f2',color:C.danger,borderRadius:4,padding:'1px 6px',fontSize:9,fontWeight:700}}>DND</span>}
                          <span style={{fontSize:11,color:C.faint}}>{l.stage}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:8,marginTop:8}}>
                    <button onClick={()=>setSelLeads(leads.filter(l=>!l.dnd).map(l=>l.id))} style={{fontSize:11,color:C.primary,background:'none',border:'none',cursor:'pointer'}}>Select all</button>
                    <span style={{color:C.border}}>·</span>
                    <button onClick={()=>setSelLeads([])} style={{fontSize:11,color:C.muted,background:'none',border:'none',cursor:'pointer'}}>Clear</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Schedule ── */}
          {step===2&&(
            <div style={{display:'grid',gap:16}}>

              {/* Send timing */}
              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:10,display:'block'}}>When to send</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {[
                    { id:'now',       label:'Send Now',          icon:'ti-send',          desc:'Send immediately after clicking send' },
                    { id:'scheduled', label:'Schedule for Later', icon:'ti-calendar-time', desc:'Pick a date, time and timezone' },
                  ].map(m=>(
                    <button key={m.id} onClick={()=>setSendMode(m.id)}
                      style={{padding:'14px 16px',borderRadius:10,border:`1px solid ${sendMode===m.id?C.primary:C.border}`,background:sendMode===m.id?'var(--color-primary-light)':'#fff',cursor:'pointer',textAlign:'left',display:'flex',gap:10,alignItems:'flex-start'}}>
                      <i className={`ti ${m.icon}`} style={{fontSize:20,color:sendMode===m.id?C.primary:C.muted,flexShrink:0,marginTop:2}}/>
                      <div>
                        <div style={{fontWeight:600,fontSize:13,color:sendMode===m.id?C.primary:C.dark}}>{m.label}</div>
                        <div style={{fontSize:11,color:C.faint,marginTop:2}}>{m.desc}</div>
                      </div>
                      {sendMode===m.id&&<i className="ti ti-circle-check" style={{color:C.primary,fontSize:16,marginLeft:'auto',flexShrink:0}}/>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date/time/timezone */}
              {sendMode==='scheduled'&&(
                <div style={{background:'#fff',borderRadius:10,border:`1px solid ${C.border}`,padding:'16px 18px'}}>
                  <div style={{fontWeight:600,fontSize:13,color:C.dark,marginBottom:12}}>Schedule details</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:12}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:500,color:C.muted,marginBottom:4,display:'block'}}>DATE</label>
                      <input type="date" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        style={{...inp}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:500,color:C.muted,marginBottom:4,display:'block'}}>TIME</label>
                      <input type="time" value={scheduledTime} onChange={e=>setScheduledTime(e.target.value)} style={{...inp}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:500,color:C.muted,marginBottom:4,display:'block'}}>TIMEZONE <span style={{color:C.faint}}>(auto-detected)</span></label>
                      <select value={schedTz} onChange={e=>setSchedTz(e.target.value)} style={{...inp}}>
                        {TIMEZONES.map(tz=><option key={tz} value={tz}>{tz.replace(/_/g,' ')}</option>)}
                      </select>
                    </div>
                  </div>
                  {scheduledAt&&(
                    <div style={{background:'#f0fdf4',borderRadius:8,padding:'10px 14px',display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#166534'}}>
                      <i className="ti ti-calendar-check" style={{fontSize:16}}/>
                      <div>
                        <strong>Scheduled:</strong>{' '}
                        {scheduledAt.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})} at{' '}
                        {scheduledAt.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} ({schedTz})
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Batch settings */}
              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:10,display:'block'}}>How to send</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {[
                    { id:'all',   label:'All at once',    icon:'ti-send',     desc:'Send to all recipients immediately' },
                    { id:'batch', label:'Send in batches', icon:'ti-packages', desc:'Spread out over time — better deliverability' },
                  ].map(m=>(
                    <button key={m.id} onClick={()=>setBatchMode(m.id)}
                      style={{padding:'14px 16px',borderRadius:10,border:`1px solid ${batchMode===m.id?C.primary:C.border}`,background:batchMode===m.id?'var(--color-primary-light)':'#fff',cursor:'pointer',textAlign:'left',display:'flex',gap:10,alignItems:'flex-start'}}>
                      <i className={`ti ${m.icon}`} style={{fontSize:20,color:batchMode===m.id?C.primary:C.muted,flexShrink:0,marginTop:2}}/>
                      <div>
                        <div style={{fontWeight:600,fontSize:13,color:batchMode===m.id?C.primary:C.dark}}>{m.label}</div>
                        <div style={{fontSize:11,color:C.faint,marginTop:2}}>{m.desc}</div>
                      </div>
                      {batchMode===m.id&&<i className="ti ti-circle-check" style={{color:C.primary,fontSize:16,marginLeft:'auto',flexShrink:0}}/>}
                    </button>
                  ))}
                </div>
                {batchMode==='batch'&&(
                  <div style={{background:'#fff',borderRadius:10,border:`1px solid ${C.border}`,padding:'14px 16px',marginTop:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                      <span style={{fontSize:13,color:C.text}}>Send</span>
                      <input type="number" min="1" max="10000" value={batchSize} onChange={e=>setBatchSize(e.target.value)}
                        style={{width:80,padding:'7px 10px',borderRadius:7,border:`1px solid ${C.border}`,fontSize:14,outline:'none',textAlign:'center',fontWeight:600}}/>
                      <span style={{fontSize:13,color:C.text}}>emails per</span>
                      <select value={batchInterval} onChange={e=>setBatchInterval(e.target.value)}
                        style={{padding:'7px 14px',borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,outline:'none',background:'#fff',fontWeight:500}}>
                        <option value="minute">Minute</option>
                        <option value="hour">Hour</option>
                        <option value="day">Day</option>
                      </select>
                    </div>
                    <div style={{fontSize:11,color:C.faint,marginTop:8}}>
                      <i className="ti ti-info-circle" style={{marginRight:4}}/>
                      With {recipientCount} contacts at {batchSize} per {batchInterval}: ~{Math.ceil(recipientCount/(parseInt(batchSize)||1))} batch{Math.ceil(recipientCount/(parseInt(batchSize)||1))>1?'es':''}. Processed every 5 minutes by the background scheduler.
                    </div>
                  </div>
                )}
              </div>

              {/* Summary card */}
              <div style={{background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,padding:'14px 16px'}}>
                <div style={{fontWeight:600,fontSize:13,color:C.dark,marginBottom:10}}>Campaign Summary</div>
                <div style={{display:'grid',gap:6,fontSize:12}}>
                  {[
                    ['Subject',    subject],
                    ['Recipients', `${recipientCount} contact${recipientCount!==1?'s':''}`+(dndCount>0?` (${dndCount} DND excluded)`:'')],
                    ['Send time',  sendMode==='now'?'Immediately after clicking send':scheduledAt?`${scheduledAt.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} at ${scheduledAt.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} ${schedTz}`:'Not set'],
                    ['Batching',   batchMode==='batch'?`${batchSize} per ${batchInterval}`:'All at once'],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:'flex',gap:8}}>
                      <span style={{color:C.muted,minWidth:90,flexShrink:0}}>{k}:</span>
                      <span style={{fontWeight:500,color:C.dark}}>{v||'—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer navigation ── */}
        <div style={{padding:'16px 24px',borderTop:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,background:'#f9fafb',borderRadius:'0 0 16px 16px'}}>
          <div style={{display:'flex',gap:8}}>
            {step>0&&(
              <button onClick={()=>setStep(s=>s-1)}
                style={{padding:'9px 18px',borderRadius:8,border:`1px solid ${C.border}`,background:'#fff',color:C.text,fontSize:13,fontWeight:500,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
                <i className="ti ti-arrow-left"/> Back
              </button>
            )}
            <button onClick={()=>handleSend(true)} disabled={saving||!subject.trim()}
              style={{padding:'9px 18px',borderRadius:8,border:`1px solid ${C.border}`,background:'#fff',color:C.muted,fontSize:13,fontWeight:500,cursor:'pointer',display:'flex',alignItems:'center',gap:6,opacity:saving?0.6:1}}>
              <i className={`ti ${saving?'ti-loader ti-spin':'ti-device-floppy'}`}/>{saving?'Saving...':'Save Draft'}
            </button>
          </div>

          <div style={{display:'flex',gap:8}}>
            {step<STEPS.length-1?(
              <button onClick={()=>setStep(s=>s+1)} disabled={!canProceed[step]}
                style={{padding:'9px 22px',borderRadius:8,border:'none',background:canProceed[step]?C.primary:'#e5e7eb',color:canProceed[step]?'#fff':'#9ca3af',fontSize:13,fontWeight:600,cursor:canProceed[step]?'pointer':'not-allowed',display:'flex',alignItems:'center',gap:6}}>
                Next <i className="ti ti-arrow-right"/>
              </button>
            ):(
              <button onClick={()=>handleSend(false)} disabled={saving||!canProceed[2]||recipientCount===0}
                style={{padding:'9px 22px',borderRadius:8,border:'none',background:saving||!canProceed[2]||recipientCount===0?'#e5e7eb':C.primary,color:saving||!canProceed[2]||recipientCount===0?'#9ca3af':'#fff',fontSize:13,fontWeight:600,cursor:saving||!canProceed[2]||recipientCount===0?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:6}}>
                <i className={`ti ${saving?'ti-loader ti-spin':sendMode==='scheduled'?'ti-calendar-time':'ti-send'}`}/>
                {saving?'Sending...' : sendMode==='scheduled' ? 'Schedule Campaign' : 'Send Campaign'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
